import { useEffect, useRef, useState } from "react";
import { getPublicBlogs, type BlogPost } from "../lib/strapi";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import gsap from "gsap";
import {
  FaArrowRight, FaBrain, FaCheckCircle, FaBuilding,
  FaGraduationCap, FaSchool, FaRobot, FaHeartbeat, FaChartLine,
  FaGlobe, FaShieldAlt, FaChartBar, FaComments,
  FaWhatsapp, FaInstagram, FaLinkedin,
  FaQuoteLeft, FaEnvelope, FaPhone, FaApple, FaAndroid,
  FaBell, FaWifi, FaMobileAlt, FaMicrochip, FaFlask, FaSeedling,
} from "react-icons/fa";
import { useLang } from "../hooks/useLang";
import { useTheme } from "../hooks/useTheme";
import ScrollFadeIn from "../components/ScrollFadeIn";
import TestimonialsCarousel from "../components/TestimonialsCarousel";
import MeshGradient from "../components/MeshGradient";
import logo from "@assets/logoMindbridge_1776100737645.png";
import heroImg from "@assets/heroMindbridge_1776102548426.jpeg";

// Place fondatrice.jpg in /public/ to display the founder's photo
const FOUNDER_PHOTO = "/fondatrice.jpg";

const GOLD = "#C9A84C";
const DARK = "#2A2E3A";

/* ─── Animated counter hook ───────────────────────────────── */
function useCounter(target: number, duration = 2000) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setVal(Math.floor(start));
      if (start >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target, duration]);

  return { val, ref };
}

/* ─── Counter Card ─────────────────────────────────────────── */
function CounterCard({ num, suffix, label }: { num: number; suffix: string; label: string }) {
  const { val, ref } = useCounter(num);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div
      ref={ref}
      className="p-6 rounded-2xl text-center flex flex-col items-center gap-2"
      style={{
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`,
      }}
    >
      <span className="text-4xl font-black" style={{ color: GOLD }}>
        {val}{suffix}
      </span>
      <span className="text-sm" style={{ color: isDark ? "#9ca3af" : "#6b7280" }}>{label}</span>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { currentLang } = useLang();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isRTL = currentLang === "ar";

  const [homePosts, setHomePosts] = useState<BlogPost[]>([]);
  useEffect(() => { getPublicBlogs().then((p) => setHomePosts(p.slice(0, 3))).catch(() => {}); }, []);

  /* ── Hero parallax ── */
  const heroSectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress: heroScrollY } = useScroll({
    target: heroSectionRef,
    offset: ["start start", "end start"],
  });
  const heroBgY = useTransform(heroScrollY, [0, 1], ["0%", "30%"]);
  const heroContentY = useTransform(heroScrollY, [0, 1], ["0%", "15%"]);
  const heroOpacity = useTransform(heroScrollY, [0, 0.7], [1, 0]);

  /* ── Scroll to section on mount (from cross-page hash navigation) ── */
  useEffect(() => {
    const target = sessionStorage.getItem("pendingScroll");
    if (target) {
      sessionStorage.removeItem("pendingScroll");
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 88;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 200);
    }
  }, []);

  /* ── GSAP Hero stagger ── */
  const heroRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!heroRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-stagger > *",
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, stagger: 0.18, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const testimonials = [
    { name: t("testimonials.t1_name"), role: t("testimonials.t1_role"), text: t("testimonials.t1_text") },
    { name: t("testimonials.t2_name"), role: t("testimonials.t2_role"), text: t("testimonials.t2_text") },
    { name: t("testimonials.t3_name"), role: t("testimonials.t3_role"), text: t("testimonials.t3_text") },
    { name: t("testimonials.t4_name"), role: t("testimonials.t4_role"), text: t("testimonials.t4_text") },
    { name: t("testimonials.t5_name"), role: t("testimonials.t5_role"), text: t("testimonials.t5_text") },
    { name: t("testimonials.t6_name"), role: t("testimonials.t6_role"), text: t("testimonials.t6_text") },
  ];

  const pourquoiCards = [
    { icon: FaBrain,        title: t("pourquoi.c1_title"), text: t("pourquoi.c1_text") },
    { icon: FaRobot,        title: t("pourquoi.c2_title"), text: t("pourquoi.c2_text") },
    { icon: FaHeartbeat,    title: t("pourquoi.c3_title"), text: t("pourquoi.c3_text") },
    { icon: FaChartLine,    title: t("pourquoi.c4_title"), text: t("pourquoi.c4_text") },
    { icon: FaGlobe,        title: t("pourquoi.c5_title"), text: t("pourquoi.c5_text") },
    { icon: FaShieldAlt,    title: t("pourquoi.c6_title"), text: t("pourquoi.c6_text") },
  ];

  const services = [
    { icon: FaGraduationCap, dir: "left",  title: t("services.s1_title"), text: t("services.s1_text"), tags: ["Entreprise", "École", "Formation"] },
    { icon: FaBrain,         dir: "right", title: t("services.s2_title"), text: t("services.s2_text"), tags: ["IA", "Psychométrie"] },
    { icon: FaChartBar,      dir: "left",  title: t("services.s3_title"), text: t("services.s3_text"), tags: ["Analytics", "IA Prédictive"] },
    { icon: FaComments,      dir: "right", title: t("services.s4_title"), text: t("services.s4_text"), tags: ["Chatbot", "Communication"] },
    { icon: FaHeartbeat,     dir: "left",  title: t("services.s5_title"), text: t("services.s5_text"), tags: ["Bien-être", "QVT", "RPS"] },
  ];

  const sectionLabel = (text: string) => (
    <span className="text-xs font-bold uppercase tracking-widest mb-3 block" style={{ color: GOLD }}>{text}</span>
  );

  const sectionDivider = (
    <div className="w-14 h-1 rounded-full mt-4" style={{ background: GOLD }} />
  );

  const cardBg = isDark ? "var(--card-bg)" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const textSecondary = isDark ? "#9ca3af" : "#6b7280";

  /* ── Ripple helper ── */
  const addRipple = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const span = document.createElement("span");
    span.className = "ripple-span";
    span.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px;`;
    btn.appendChild(span);
    span.addEventListener("animationend", () => span.remove());
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} style={{ background: "var(--page-bg)" }}>

      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <section
        ref={heroSectionRef}
        id="accueil"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Parallax background image */}
        <motion.div
          className="absolute inset-0"
          style={{
            y: heroBgY,
            backgroundImage: `url(${heroImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            scale: 1.1,
          }}
        />

        {/* Vignette overlay — dark edges, clear center so hero image is fully visible */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 70% at 50% 45%, rgba(15,17,23,0.08) 0%, rgba(15,17,23,0.55) 75%, rgba(15,17,23,0.85) 100%)" }} />
        {/* Bottom fade for footer readability */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,17,23,0.80) 0%, transparent 35%)" }} />
        {/* Top fade for navbar readability */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(15,17,23,0.45) 0%, transparent 25%)" }} />

        {/* Mesh gradient blobs */}
        <MeshGradient />

        {/* Floating geometric shapes */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 2 }}>
          <div className="geo-shape-1 absolute w-40 h-40 sm:w-64 sm:h-64 md:w-80 md:h-80 rounded-full border-2 border-[#C9A84C]" style={{ top: "8%", left: "2%" }} />
          <div className="geo-shape-4 absolute w-32 h-32 sm:w-52 sm:h-52 md:w-64 md:h-64 rounded-full" style={{
            bottom: "10%", right: "5%",
            background: `radial-gradient(circle, ${GOLD}20 0%, transparent 70%)`,
          }} />
        </div>

        {/* Content — centered single column */}
        <motion.div
          ref={heroRef}
          style={{ y: heroContentY, opacity: heroOpacity }}
          className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24 text-center"
        >
          <div className="hero-stagger flex flex-col gap-6 items-center">

            {/* Logo */}
            <img src={logo} alt="MindBridge" className="h-20 w-20 object-contain drop-shadow-2xl" />

            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-semibold tracking-wide"
              style={{ borderColor: `${GOLD}50`, background: `${GOLD}18`, color: GOLD, backdropFilter: "blur(8px)" }}
            >
              EdTech · IA · Neurosciences · Psychométrie
            </div>

            {/* Title */}
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-black leading-tight text-white max-w-3xl"
              style={{ fontFamily: isRTL ? "'Cairo', sans-serif" : "'Raleway', sans-serif", textShadow: "0 2px 20px rgba(0,0,0,0.5)" }}
            >
              {t("hero.title")}
            </h1>

            {/* Subtitle */}
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl" style={{ opacity: 0.88, textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>
              {t("hero.subtitle")}
            </p>

            {/* CTA buttons */}
            <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 pt-2 w-full sm:w-auto ${isRTL ? "sm:flex-row-reverse" : ""}`}>
              <Link
                href="/entreprise"
                className="glow-btn px-6 sm:px-9 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105 text-center"
                style={{ background: GOLD, color: DARK }}
                onClick={addRipple}
              >
                {t("hero.cta1")}
              </Link>
              <Link
                href="/contact"
                className="px-6 sm:px-9 py-3.5 rounded-xl font-bold text-sm border-2 border-white/30 text-white hover:border-[#C9A84C] hover:text-[#C9A84C] transition-all duration-200 flex items-center justify-center gap-2"
                style={{ backdropFilter: "blur(8px)", background: "rgba(255,255,255,0.05)" }}
                onClick={addRipple}
              >
                {t("hero.cta2")}
                <FaArrowRight size={12} className={isRTL ? "rotate-180" : ""} />
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 pt-2">
              {[
                { icon: FaCheckCircle, label: isRTL ? "إدارة RGPD" : "RGPD compliant" },
                { icon: FaGlobe, label: isRTL ? "٣ لغات" : "3 langues" },
                { icon: FaShieldAlt, label: isRTL ? "بيانات آمنة" : "Données sécurisées" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-2 text-xs font-medium" style={{ color: "#b0b8c8" }}>
                  <Icon size={13} style={{ color: GOLD }} /> {label}
                </span>
              ))}
            </div>

            {/* Floating learner badge */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
              className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl mt-2"
              style={{ background: "rgba(42,46,58,0.85)", backdropFilter: "blur(12px)", border: `1px solid ${GOLD}40` }}
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white text-xs font-semibold">
                {isRTL ? "+٥٠٠ متعلم نشط" : currentLang === "en" ? "500+ active learners" : "500+ apprenants actifs"}
              </span>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 scroll-bounce" style={{ zIndex: 10 }}>
          <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-1 h-1.5 bg-[#C9A84C] rounded-full" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          ABOUT  #apropos
      ══════════════════════════════════════════ */}
      <section id="apropos" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--section-alt)" }}>
        <div className="max-w-7xl mx-auto">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isRTL ? "lg:flex-row-reverse" : ""}`}>

            {/* Text */}
            <ScrollFadeIn direction={isRTL ? "right" : "left"}>
              {sectionLabel(t("navbar.apropos"))}
              <h2 className="text-3xl md:text-4xl font-bold mt-1 mb-5" style={{ color: "var(--text-primary)" }}>
                {t("about.title")}
              </h2>
              <p className="leading-relaxed text-base mb-8" style={{ color: textSecondary }}>
                {t("about.text")}
              </p>
              {/* Feature pills */}
              <div className="flex flex-wrap gap-2">
                {[
                  t("ce_que_nous_faisons.item1_title"),
                  t("ce_que_nous_faisons.item2_title"),
                  t("ce_que_nous_faisons.item3_title"),
                  t("ce_que_nous_faisons.item4_title"),
                ].map((pill) => (
                  <span key={pill} className="tag-pill">{pill}</span>
                ))}
              </div>
            </ScrollFadeIn>

            {/* Stats */}
            <ScrollFadeIn direction={isRTL ? "left" : "right"} delay={0.2}>
              <div className="grid grid-cols-1 gap-4">
                <CounterCard num={500} suffix="+" label={t("about.stat1_label")} />
                <CounterCard num={98} suffix="%" label={t("about.stat2_label")} />
                <CounterCard num={12} suffix="" label={t("about.stat3_label")} />
              </div>
            </ScrollFadeIn>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CE QUE NOUS FAISONS
      ══════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--page-bg)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("ce_que_nous_faisons.title"))}
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              {t("ce_que_nous_faisons.title")}
            </h2>
            {sectionDivider}
          </ScrollFadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: FaCheckCircle, title: t("ce_que_nous_faisons.item1_title"), text: t("ce_que_nous_faisons.item1_text") },
              { icon: FaBrain, title: t("ce_que_nous_faisons.item2_title"), text: t("ce_que_nous_faisons.item2_text") },
              { icon: FaHeartbeat, title: t("ce_que_nous_faisons.item3_title"), text: t("ce_que_nous_faisons.item3_text") },
              { icon: FaComments, title: t("ce_que_nous_faisons.item4_title"), text: t("ce_que_nous_faisons.item4_text") },
            ].map((card, i) => (
              <ScrollFadeIn key={i} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -6, borderLeftColor: GOLD, boxShadow: `0 20px 40px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.12)"}` }}
                  className="p-7 rounded-2xl border-l-4 transition-all duration-300 flex gap-5"
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderLeft: `4px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${GOLD}18` }}
                  >
                    <card.icon size={20} style={{ color: GOLD }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-base mb-2" style={{ color: "var(--text-primary)" }}>{card.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{card.text}</p>
                  </div>
                </motion.div>
              </ScrollFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          OFFRES  #offres
      ══════════════════════════════════════════ */}
      <section id="offres" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--section-alt)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("navbar.offres"))}
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              {t("offers.title")}
            </h2>
            {sectionDivider}
          </ScrollFadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: FaBuilding, key: "card1", href: "/entreprise" },
              { icon: FaGraduationCap, key: "card2", href: "/formations" },
              { icon: FaSchool, key: "card3", href: "/ecole" },
            ].map((offer, i) => (
              <ScrollFadeIn key={i} delay={i * 0.15}>
                <motion.div
                  whileHover={{ y: -8, borderColor: GOLD }}
                  className="group p-8 rounded-2xl border text-center flex flex-col items-center gap-4 transition-all duration-300 h-full"
                  style={{ background: isDark ? DARK : "#fff", border: `1px solid ${cardBorder}` }}
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${GOLD}18` }}>
                    <offer.icon size={28} style={{ color: GOLD }} />
                  </div>
                  <h3 className="font-bold text-xl" style={{ color: "var(--text-primary)" }}>
                    {t(`offers.${offer.key}_title`)}
                  </h3>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: textSecondary }}>
                    {t(`offers.${offer.key}_text`)}
                  </p>
                  <Link
                    href={offer.href}
                    className="mt-2 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all hover:scale-105"
                    style={{ color: GOLD, borderColor: `${GOLD}50`, background: `${GOLD}10` }}
                  >
                    {t("offers.learn_more")}
                    <FaArrowRight size={11} className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180" : ""}`} />
                  </Link>
                </motion.div>
              </ScrollFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          POURQUOI NOUS CHOISIR  #pourquoi
      ══════════════════════════════════════════ */}
      <section id="pourquoi" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--page-bg)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("pourquoi.label"))}
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>
              Ce qui nous rend différents
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: textSecondary }}>
              MindBridge réunit dans une seule plateforme ce que les autres offrent séparément.
            </p>
            {sectionDivider}
          </ScrollFadeIn>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } }, hidden: {} }}
          >
            {pourquoiCards.map((card, i) => (
              <motion.div
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
                }}
                whileHover={{ y: -6, borderColor: GOLD }}
                className="p-7 rounded-2xl border transition-all duration-300"
                style={{ background: isDark ? DARK : "#fff", border: `1px solid ${cardBorder}` }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${GOLD}18` }}>
                  <card.icon size={22} style={{ color: GOLD }} />
                </div>
                <h3 className="font-bold text-base mb-2" style={{ color: "var(--text-primary)" }}>{card.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{card.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SERVICES (zigzag)  #services
      ══════════════════════════════════════════ */}
      <section id="services" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--section-alt)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("services.label"))}
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>
              Des solutions sur-mesure pour chaque besoin
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: textSecondary }}>
              De la digitalisation des contenus à l'analyse prédictive, MindBridge couvre tout le cycle de l'apprentissage.
            </p>
            {sectionDivider}
          </ScrollFadeIn>

          <div className="space-y-16">
            {services.map((svc, i) => {
              const iconLeft = svc.dir === "left";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: iconLeft ? -60 : 60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.65, ease: "easeOut" }}
                  className={`flex flex-col md:flex-row items-center gap-10 ${!iconLeft ? "md:flex-row-reverse" : ""}`}
                >
                  {/* Icon block */}
                  <div className="flex-shrink-0 flex items-center justify-center w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-3xl" style={{ background: `${GOLD}12`, border: `2px solid ${GOLD}30` }}>
                    <svc.icon size={64} style={{ color: GOLD }} />
                  </div>

                  {/* Text block */}
                  <div className={`flex-1 ${!iconLeft && !isRTL ? "md:text-right" : ""}`}>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{svc.title}</h3>
                    <p className="leading-relaxed mb-5 text-base" style={{ color: textSecondary }}>{svc.text}</p>
                    <div className={`flex flex-wrap gap-2 ${!iconLeft && !isRTL ? "md:justify-end" : ""}`}>
                      {svc.tags.map((tag) => (
                        <span key={tag} className="tag-pill">{tag}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          VISION PILLARS
      ══════════════════════════════════════════ */}
      <section id="vision" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--page-bg)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("vision_section.title"))}
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              {t("vision_section.subtitle")}
            </h2>
            {sectionDivider}
          </ScrollFadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: t("vision_section.pilier1_title"), text: t("vision_section.pilier1_text"), Icon: FaMicrochip },
              { title: t("vision_section.pilier2_title"), text: t("vision_section.pilier2_text"), Icon: FaFlask },
              { title: t("vision_section.pilier3_title"), text: t("vision_section.pilier3_text"), Icon: FaSeedling },
            ].map((pilier, i) => (
              <ScrollFadeIn key={i} delay={i * 0.15}>
                <motion.div
                  whileHover={{ y: -6, borderColor: GOLD }}
                  className="p-8 rounded-2xl border h-full transition-all duration-300"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}30` }}
                  >
                    <pilier.Icon size={26} style={{ color: GOLD }} />
                  </div>
                  <h3 className="font-bold text-lg mb-3" style={{ color: "var(--text-primary)" }}>{pilier.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{pilier.text}</p>
                </motion.div>
              </ScrollFadeIn>
            ))}
          </div>

          {/* Bouton → page Vision */}
          <ScrollFadeIn className="text-center mt-12">
            <Link
              href="/vision"
              className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
              style={{ background: GOLD, color: DARK, boxShadow: `0 6px 24px ${GOLD}45` }}
            >
              {isRTL ? "اكتشف رؤيتنا الكاملة" : currentLang === "en" ? "Discover Our Full Vision" : "Découvrir notre vision complète"}
              <FaArrowRight size={13} className={isRTL ? "rotate-180" : ""} />
            </Link>
          </ScrollFadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          PROCESSUS  #processus
      ══════════════════════════════════════════ */}
      <section
        id="processus"
        className="relative py-16 sm:py-22 lg:py-32 px-4 sm:px-6 overflow-hidden"
        style={{ background: isDark ? "#080b12" : "#edf1fa" }}
      >
        {/* ── Animated ambient blobs ── */}
        <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
          <div style={{ position:"absolute", top:"5%", left:"-5%", width:600, height:600, borderRadius:"60% 40% 70% 30%/50% 60% 40% 50%", background:`radial-gradient(ellipse, ${GOLD}0D 0%, transparent 65%)`, filter:"blur(90px)" }} />
          <div style={{ position:"absolute", bottom:"0%", right:"-8%", width:500, height:500, background:"radial-gradient(ellipse, rgba(99,102,241,0.09) 0%, transparent 65%)", filter:"blur(80px)" }} />
          <div style={{ position:"absolute", top:"40%", left:"38%", width:350, height:350, background:`radial-gradient(ellipse, ${GOLD}07 0%, transparent 70%)`, filter:"blur(60px)" }} />

          {/* Dot-grid overlay */}
          <div style={{ position:"absolute", inset:0, backgroundImage:`radial-gradient(circle at 1.5px 1.5px, ${GOLD}18 1px, transparent 0)`, backgroundSize:"48px 48px", opacity:0.5 }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">

          {/* Header */}
          <ScrollFadeIn className="text-center mb-20">
            {sectionLabel(t("processus.label"))}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-5 leading-tight" style={{ color:"var(--text-primary)" }}>
              {t("processus.title_before")}{" "}
              <span style={{ color: GOLD, textShadow: `0 0 32px ${GOLD}60` }}>
                {t("processus.title_highlight")}
              </span>
              {t("processus.title_after") ? <>{" "}{t("processus.title_after")}</> : null}
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: textSecondary }}>
              {t("processus.subtitle")}
            </p>
            {sectionDivider}
          </ScrollFadeIn>

          {/* ── Steps ── */}
          <div className="relative">

            {/* Desktop connecting line */}
            <div className="hidden lg:block absolute" style={{ top: 56, left: "13%", right: "13%", height: 2, background: `linear-gradient(90deg, transparent, ${GOLD}30, ${GOLD}60, ${GOLD}30, transparent)` }}>
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: "easeInOut", delay: 0.5 }}
                className="h-full origin-left"
                style={{ background: `linear-gradient(90deg, ${GOLD}90, ${GOLD})` }}
              />
              {/* Travelling glow dot */}
              <motion.div
                initial={{ left: 0 }}
                whileInView={{ left: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: "easeInOut", delay: 0.5 }}
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                style={{ background: GOLD, boxShadow: `0 0 12px ${GOLD}, 0 0 24px ${GOLD}80` }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {[
                { num: "01", icon: FaBrain,        title: t("processus.s1_title"), text: t("processus.s1_text"), delay: 0 },
                { num: "02", icon: FaRobot,        title: t("processus.s2_title"), text: t("processus.s2_text"), delay: 0.15 },
                { num: "03", icon: FaGraduationCap,title: t("processus.s3_title"), text: t("processus.s3_text"), delay: 0.3 },
                { num: "04", icon: FaChartLine,    title: t("processus.s4_title"), text: t("processus.s4_text"), delay: 0.45 },
              ].map((step, i) => (
                <ScrollFadeIn key={i} delay={step.delay}>
                  <motion.div
                    whileHover={{ y: -10 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="process-card group relative flex flex-col items-center text-center p-8 rounded-2xl cursor-default h-full"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.85)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
                      backdropFilter: "blur(8px)",
                      boxShadow: isDark ? "0 4px 30px rgba(0,0,0,0.4)" : "0 4px 24px rgba(0,0,0,0.06)",
                    }}
                    >
                    {/* Pulsing number badge */}
                    <div className="relative mb-6">
                      <motion.div
                        animate={{ scale: [1, 1.35, 1], opacity: [0.35, 0.08, 0.35] }}
                        transition={{ repeat: Infinity, duration: 2.6 + i * 0.5, ease: "easeInOut" }}
                        className="absolute -inset-4 rounded-full"
                        style={{ background: `${GOLD}35` }}
                      />
                      <div
                        className="relative w-14 h-14 rounded-full flex items-center justify-center text-xl font-black"
                        style={{
                          background: `linear-gradient(135deg, ${GOLD}25, ${GOLD}08)`,
                          color: GOLD,
                          border: `2px solid ${GOLD}60`,
                          boxShadow: `0 0 20px ${GOLD}30, inset 0 0 12px ${GOLD}10`,
                        }}
                      >
                        {step.num}
                      </div>
                    </div>

                    {/* Icon */}
                    <motion.div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                      style={{
                        background: `linear-gradient(135deg, ${GOLD}20, ${GOLD}08)`,
                        border: `1px solid ${GOLD}30`,
                      }}
                      whileHover={{ rotate: [0, -8, 8, 0] }}
                      transition={{ duration: 0.4 }}
                    >
                      <step.icon size={24} style={{ color: GOLD }} />
                    </motion.div>

                    <h3 className="font-black text-lg mb-3" style={{ color: "var(--text-primary)" }}>
                      {step.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>
                      {step.text}
                    </p>

                    {/* Gold bottom accent line */}
                    <div
                      className="absolute bottom-0 left-6 right-6 h-0.5 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                      style={{ background: `linear-gradient(90deg, ${GOLD}, ${GOLD}40)` }}
                    />
                  </motion.div>
                </ScrollFadeIn>
              ))}
            </div>
          </div>

          {/* ── Bottom metric row ── */}
          <motion.div
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.12 } }, hidden: {} }}
          >
            {[
              { value: "40%", label: t("processus.m1_label") },
              { value: "35%", label: t("processus.m2_label") },
              { value: "25%", label: t("processus.m3_label") },
              { value: "98%", label: t("processus.m4_label") },
            ].map(({ value, label }, i) => (
              <motion.div
                key={i}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                className="text-center p-5 rounded-2xl"
                style={{
                  background: isDark ? "rgba(201,168,76,0.06)" : "rgba(201,168,76,0.08)",
                  border: `1px solid ${GOLD}25`,
                }}
              >
                <div className="text-3xl font-black mb-1" style={{ color: GOLD, textShadow: `0 0 20px ${GOLD}50` }}>
                  {value}
                </div>
                <div className="text-xs font-medium" style={{ color: textSecondary }}>
                  {label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          MOT DE LA FONDATRICE  #fondatrice
      ══════════════════════════════════════════ */}
      <section id="fondatrice" className="relative overflow-hidden lg:min-h-[680px]">

        {/* ── Split layout: photo LEFT | content RIGHT ── */}
        <div className={`flex flex-col lg:flex-row lg:min-h-[680px] ${isRTL ? "lg:flex-row-reverse" : ""}`}>

          {/* ════ PHOTO PANEL (full-height, ~45% width) ════ */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 60 : -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative lg:w-[45%] h-64 sm:h-72 md:h-80 lg:h-auto overflow-hidden flex-shrink-0"
          >
            {/* Fallback gradient background */}
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(160deg, #1a1d26 0%, #0f1117 100%)` }}
            />
            {/* Fallback decorative initials */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
              <span className="text-[8rem] font-black leading-none" style={{ color: `${GOLD}18` }}>M</span>
            </div>

            {/* The founder photo — full cover */}
            <img
              src={FOUNDER_PHOTO}
              alt="Fondatrice MindBridge"
              className="absolute inset-0 w-full h-full object-cover object-center"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />

            {/* Right-edge fade into dark content panel */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: isRTL
                  ? "linear-gradient(to left, transparent 55%, #0d101a 100%)"
                  : "linear-gradient(to right, transparent 55%, #0d101a 100%)",
              }}
            />
            {/* Bottom-edge fade (mobile) */}
            <div
              className="absolute inset-0 pointer-events-none lg:hidden"
              style={{ background: "linear-gradient(to bottom, transparent 40%, #0d101a 100%)" }}
            />

            {/* Gold left border line */}
            <div
              className="absolute top-0 bottom-0 w-[3px] pointer-events-none"
              style={{
                [isRTL ? "right" : "left"]: 0,
                background: `linear-gradient(to bottom, transparent, ${GOLD}, transparent)`,
              }}
            />

            {/* Floating name card at the bottom of photo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="absolute bottom-6 inset-x-6 lg:inset-x-8 z-10"
            >
              <div
                className="inline-flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{
                  background: "rgba(10,11,18,0.82)",
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${GOLD}35`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${GOLD}, #e8c56d)` }}
                >
                  <FaQuoteLeft size={13} color={DARK} />
                </div>
                <div>
                  <div className="text-white text-sm font-bold leading-tight">{t("fondatrice.signature")}</div>
                  <div className="text-xs font-medium mt-0.5" style={{ color: GOLD }}>Fondatrice &amp; CEO · MindBridge EdTech</div>
                </div>
                <a
                  href="https://www.linkedin.com/company/mindbridge-edtech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn MindBridge EdTech"
                  className="ml-auto w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-transform hover:scale-110"
                  style={{ background: "#0A66C2" }}
                >
                  <FaLinkedin size={14} color="#fff" />
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* ════ CONTENT PANEL (right, dark) ════ */}
          <div
            className="relative flex-1 flex flex-col justify-center px-5 sm:px-8 md:px-14 lg:px-16 py-14 sm:py-20 lg:py-24"
            style={{ background: "#0d101a" }}
          >
            {/* Radial glow behind content */}
            <div
              className="absolute pointer-events-none"
              style={{
                width: 500, height: 500, borderRadius: "50%",
                background: `radial-gradient(circle, ${GOLD}08 0%, transparent 70%)`,
                top: "40%", [isRTL ? "right" : "left"]: "10%",
                transform: "translate(-50%, -50%)",
              }}
            />

            {/* Section label */}
            <ScrollFadeIn>
              <span className="text-xs font-bold uppercase tracking-[0.4em] block mb-6" style={{ color: GOLD }}>
                {t("fondatrice.section_label")}
              </span>
            </ScrollFadeIn>

            {/* Big decorative quote icon */}
            <ScrollFadeIn delay={0.1}>
              <FaQuoteLeft size={44} style={{ color: GOLD, opacity: 0.55 }} className="mb-6" />
            </ScrollFadeIn>

            {/* The quote — large and prominent */}
            <ScrollFadeIn delay={0.2}>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.85, delay: 0.25 }}
                className="text-lg sm:text-xl md:text-2xl lg:text-[1.7rem] leading-relaxed font-light text-white mb-8 sm:mb-10 relative z-10"
                style={{
                  fontFamily: isRTL ? "'Cairo', sans-serif" : "'Raleway', sans-serif",
                  textShadow: "0 2px 30px rgba(0,0,0,0.4)",
                }}
              >
                {t("fondatrice.quote")}
              </motion.p>
            </ScrollFadeIn>

            {/* Gold separator line — animated */}
            <ScrollFadeIn delay={0.35}>
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="h-[2px] w-20 mb-8 origin-left rounded-full"
                style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
              />
            </ScrollFadeIn>

            {/* Signature */}
            <ScrollFadeIn delay={0.45}>
              <div className="text-xl font-bold" style={{ color: GOLD }}>
                {t("fondatrice.signature")}
              </div>
              <div className="text-xs font-semibold tracking-[0.25em] uppercase mt-1" style={{ color: "#6b7280" }}>
                Fondatrice &amp; CEO · MindBridge EdTech
              </div>
            </ScrollFadeIn>

            {/* Bottom gold line */}
            <div className="absolute bottom-0 inset-x-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}50, transparent)` }} />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TESTIMONIALS  #temoignages
      ══════════════════════════════════════════ */}
      <section id="temoignages" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--section-alt)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("navbar.temoignages"))}
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              {t("testimonials.title")}
            </h2>
            {sectionDivider}
          </ScrollFadeIn>
          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* ══════════════════════════════════════════
          BLOG  #blog
      ══════════════════════════════════════════ */}
      <section id="blog" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--page-bg)" }}>
        <div className="max-w-7xl mx-auto">
          <ScrollFadeIn className="text-center mb-14">
            {sectionLabel(t("navbar.blog"))}
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>
              {t("blog.title")}
            </h2>
            {sectionDivider}
          </ScrollFadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {homePosts.length === 0
              ? Array.from({ length: 3 }).map((_, i) => (
                  <ScrollFadeIn key={i} delay={i * 0.15}>
                    <div className="rounded-2xl overflow-hidden border animate-pulse h-72"
                      style={{ background: cardBg, border: `1px solid ${cardBorder}` }} />
                  </ScrollFadeIn>
                ))
              : homePosts.map((post, i) => (
                  <ScrollFadeIn key={post.id} delay={i * 0.15}>
                    <Link href={`/blog/${post.documentId}`}>
                      <motion.div
                        whileHover={{ y: -8, borderColor: GOLD }}
                        className="group rounded-2xl overflow-hidden border cursor-pointer flex flex-col h-full transition-all duration-300"
                        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                      >
                        <div className="h-48 flex items-center justify-center relative overflow-hidden"
                          style={{ background: `linear-gradient(135deg, ${DARK} 0%, ${GOLD}22 100%)` }}>
                          {post.image
                            ? <img src={post.image.url} alt={post.title} className="w-full h-full object-cover" />
                            : <FaBrain size={52} style={{ color: GOLD, opacity: 0.55 }} />
                          }
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <span className="text-xs font-semibold mb-2" style={{ color: GOLD }}>{post.date}</span>
                          <h3 className="font-bold text-lg mb-3 leading-snug flex-1" style={{ color: "var(--text-primary)" }}>{post.title}</h3>
                          <span className="flex items-center gap-2 text-sm font-semibold mt-auto" style={{ color: GOLD }}>
                            {t("blog.read_more")}
                            <FaArrowRight size={12} className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180" : ""}`} />
                          </span>
                        </div>
                      </motion.div>
                    </Link>
                  </ScrollFadeIn>
                ))
            }
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SUIVEZ-NOUS
      ══════════════════════════════════════════ */}
      <section className="py-14 sm:py-18 lg:py-24 px-4 sm:px-6" style={{ background: "var(--section-alt)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <ScrollFadeIn>
            {sectionLabel(t("suivez_nous.title"))}
            <h2 className="text-3xl md:text-4xl font-bold mb-12" style={{ color: "var(--text-primary)" }}>
              {t("suivez_nous.title")}
            </h2>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: FaWhatsapp,  href: "https://wa.me/212661710694",                          label: "WhatsApp"  },
                { icon: FaInstagram, href: "https://instagram.com/mindbridge_edtech",             label: "Instagram" },
                { icon: FaLinkedin,  href: "https://www.linkedin.com/company/mindbridge-edtech/", label: "LinkedIn"  },
              ].map(({ icon: Icon, href, label }) => (
                <motion.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  whileHover={{ scale: 1.12, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center gap-3 group"
                >
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                    style={{
                      borderColor: `${GOLD}50`,
                      background: `${GOLD}10`,
                    }}
                  >
                    <Icon size={28} style={{ color: GOLD }} className="group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: textSecondary }}>{label}</span>
                </motion.a>
              ))}
            </div>
          </ScrollFadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          APP MOCKUP  #application
      ══════════════════════════════════════════ */}
      <section id="application" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6" style={{ background: "var(--page-bg)" }}>
        <div className="max-w-7xl mx-auto">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isRTL ? "lg:flex-row-reverse" : ""}`}>

            {/* Text */}
            <ScrollFadeIn direction={isRTL ? "right" : "left"}>
              {sectionLabel(t("app.label"))}
              <h2 className="text-3xl md:text-4xl font-bold mb-5" style={{ color: "var(--text-primary)" }}>
                {t("app.title")}
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: textSecondary }}>
                {t("app.subtitle")}
              </p>
              {/* Feature bullets */}
              <ul className="space-y-3 mb-10">
                {[
                  { icon: FaMobileAlt, label: t("app.f1") },
                  { icon: FaBell,      label: t("app.f2") },
                  { icon: FaChartLine, label: t("app.f3") },
                  { icon: FaWifi,      label: t("app.f4") },
                ].map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm" style={{ color: isDark ? "#d1d5db" : "#374151" }}>
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `${GOLD}18` }}
                    >
                      <Icon size={13} style={{ color: GOLD }} />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>
              {/* Download buttons */}
              <div className="flex flex-wrap gap-4">
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-3 px-6 py-3.5 rounded-xl font-bold text-sm transition-all"
                  style={{ background: GOLD, color: DARK, boxShadow: `0 4px 18px ${GOLD}40` }}
                >
                  <FaApple size={18} />
                  App Store
                </motion.a>
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-3 px-6 py-3.5 rounded-xl font-bold text-sm border-2 transition-all"
                  style={{ borderColor: `${GOLD}60`, color: GOLD, background: "transparent" }}
                >
                  <FaAndroid size={16} />
                  Google Play
                </motion.a>
              </div>
            </ScrollFadeIn>

            {/* CSS Phone Mockup */}
            <ScrollFadeIn direction={isRTL ? "left" : "right"} delay={0.2}>
              <div className="flex justify-center">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="relative w-[200px] sm:w-[240px]"
                >
                  {/* Phone outer frame */}
                  <div
                    className="relative rounded-[3rem] overflow-hidden shadow-2xl w-full"
                    style={{
                      height: 490,
                      background: DARK,
                      border: `3px solid ${GOLD}50`,
                      boxShadow: `0 30px 80px ${GOLD}20, 0 0 0 1px rgba(255,255,255,0.05)`,
                    }}
                  >
                    {/* Notch */}
                    <div
                      className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full"
                      style={{ width: 80, height: 18, background: "#0f1117", zIndex: 10 }}
                    />
                    {/* Screen content */}
                    <div className="absolute inset-0 flex flex-col" style={{ background: "#0f1117", padding: "50px 12px 16px" }}>
                      {/* App header */}
                      <div className="flex items-center gap-2 mb-5">
                        <img src={logo} alt="" className="w-7 h-7 object-contain" />
                        <span className="text-white text-xs font-bold">MindBridge</span>
                        <span className="ml-auto w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      </div>
                      {/* Progress bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-[9px] text-gray-500 mb-1">
                          <span>Module 3/8</span><span style={{ color: GOLD }}>72%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10">
                          <div className="h-full rounded-full" style={{ width: "72%", background: GOLD }} />
                        </div>
                      </div>
                      {/* Mini cards */}
                      {[
                        { label: "Neurosciences", val: "En cours" },
                        { label: "Psychométrie", val: "Complété ✓" },
                        { label: "Analytics IA", val: "À venir" },
                      ].map((c) => (
                        <div
                          key={c.label}
                          className="mb-2 p-2.5 rounded-xl"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
                        >
                          <div className="text-[9px] text-gray-400 mb-0.5">{c.label}</div>
                          <div className="text-[10px] font-semibold" style={{ color: GOLD }}>{c.val}</div>
                        </div>
                      ))}
                      {/* Bottom bar */}
                      <div className="mt-auto pt-2 border-t border-white/5 flex justify-around">
                        {[FaGraduationCap, FaBrain, FaChartLine, FaComments].map((Icon, i) => (
                          <div key={i} className="flex flex-col items-center gap-0.5">
                            <Icon size={14} style={{ color: i === 0 ? GOLD : "#6b7280" }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Gold glow */}
                  <div
                    className="absolute -bottom-6 left-1/2 -translate-x-1/2 blur-2xl rounded-full"
                    style={{ width: 160, height: 40, background: `${GOLD}35` }}
                  />
                </motion.div>
              </div>
            </ScrollFadeIn>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FINAL CTA BANNER
      ══════════════════════════════════════════ */}
      <motion.section
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative py-16 sm:py-20 lg:py-28 px-4 sm:px-6 overflow-hidden"
        style={{ background: DARK }}
      >
        {/* Animated gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, #2A2E3A, #1a1e2a, #2A2E3A)",
            backgroundSize: "200% 200%",
            animation: "gradientShift 8s ease infinite",
          }}
        />
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, ${GOLD} 1px, transparent 0)`,
            backgroundSize: "44px 44px",
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          {/* Animated envelope */}
          <motion.div
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            className="inline-flex mb-6"
          >
            <FaEnvelope size={44} style={{ color: GOLD }} />
          </motion.div>

          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD }}>
            {t("cta_final.question")}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
            {t("cta_final.action")}
          </h2>
          <p className="text-gray-300 text-base sm:text-lg mb-10">
            {t("cta_final.sub")}
          </p>

          <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 ${isRTL ? "sm:flex-row-reverse" : ""}`}>
            <motion.a
              href="tel:+212661710694"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 rounded-xl font-bold text-sm sm:text-base"
              style={{ background: GOLD, color: DARK, boxShadow: `0 8px 28px ${GOLD}45` }}
            >
              <FaPhone size={16} /> +212.661.71.06.94
            </motion.a>
            <motion.a
              href="mailto:contact@mind-bridge.education"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 rounded-xl font-bold text-sm sm:text-base border-2 border-white/30 text-white hover:border-[#C9A84C]/60 transition-all break-all"
            >
              <FaEnvelope size={16} className="flex-shrink-0" /> <span className="truncate">contact@mind-bridge.education</span>
            </motion.a>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
